import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

// Reusable tenant-scoped contact picker: search /contacts/ by name/email/phone,
// inline "New contact" creation, optional raw-email fallback for true externals
// (opposing counsel, court staff) who should NOT become firm contacts.
// onSelect receives { contact_id, name, email, phone } — contact_id is null only
// for the raw-email fallback. The input clears after each pick so it can feed
// multi-add surfaces (attendee lists) as well as single-select forms.
const ContactPicker = ({
  onSelect,
  placeholder = 'Search contacts by name, email, or phone…',
  allowRawEmail = false,
  excludeEmails = [],
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [creating, setCreating] = useState(false);

  const inputRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await api.get('/contacts/', { params: { search: query.trim(), limit: 15 } });
        const rows = Array.isArray(r.data) ? r.data : (r.data.contacts || []);
        const excluded = excludeEmails.map((e) => (e || '').toLowerCase());
        setResults(rows.filter((c) => !excluded.includes((c.primary_email_address || '').toLowerCase())));
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const contactLine = (c) =>
    [c.contact_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
     c.primary_email_address, c.primary_phone_number].filter(Boolean).join(' — ');

  const pick = (person) => {
    onSelect(person);
    setQuery('');
    setResults([]);
    setOpen(false);
    setShowNewContact(false);
    inputRef.current?.focus();
  };

  const pickContact = (c) => pick({
    contact_id: c.id,
    name: c.contact_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
    first_name: c.first_name || null,
    last_name: c.last_name || null,
    email: c.primary_email_address || null,
    phone: c.primary_phone_number || null,
  });

  const rawEmailValid = allowRawEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.trim());

  const handleCreateContact = async () => {
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) {
      setError('New contact needs first and last name');
      return;
    }
    if (!newContact.email.trim() && !newContact.phone.trim()) {
      setError('New contact needs an email or a phone number');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const payload = {
        contact_type: 'Client',
        first_name: newContact.first_name.trim(),
        last_name: newContact.last_name.trim(),
        is_client: true,
        client_status: 'active',
      };
      if (newContact.email.trim()) payload.primary_email_address = newContact.email.trim();
      if (newContact.phone.trim()) payload.primary_phone_number = newContact.phone.trim();
      const r = await api.post('/contacts/', payload);
      const created = r.data.contact || r.data;
      setNewContact({ first_name: '', last_name: '', email: '', phone: '' });
      pick({
        contact_id: created.id,
        name: created.contact_name || `${payload.first_name} ${payload.last_name}`,
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.primary_email_address || null,
        phone: payload.primary_phone_number || null,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create contact');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        autoFocus={autoFocus}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setError(null); }}
        onFocus={() => setOpen(true)}
        className="w-full px-4 py-2 bg-slate-800/50 border border-cyan-500/30 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 placeholder-cyan-400/50 text-sm"
        placeholder={placeholder}
      />

      {open && (searching || results.length > 0 || rawEmailValid || query.trim().length >= 2) && (
        <div className="absolute z-30 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {searching && <p className="px-4 py-2 text-sm text-cyan-400">Searching…</p>}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pickContact(c)}
              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-cyan-500/20 transition-colors truncate"
            >
              {contactLine(c)}
            </button>
          ))}
          {!searching && results.length === 0 && query.trim().length >= 2 && (
            <p className="px-4 py-2 text-sm text-cyan-400/60">No matching contacts.</p>
          )}
          {rawEmailValid && (
            <button
              type="button"
              onClick={() => pick({ contact_id: null, name: query.trim().split('@')[0], email: query.trim(), phone: null })}
              className="w-full px-4 py-2 text-left text-sm text-amber-300 hover:bg-amber-500/20 transition-colors border-t border-cyan-500/20"
            >
              Use external email: {query.trim()}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setShowNewContact(!showNewContact); setError(null); }}
            className="w-full px-4 py-2 text-left text-sm text-cyan-300 hover:bg-cyan-500/20 transition-colors border-t border-cyan-500/20"
          >
            {showNewContact ? '▾ New contact' : '▸ New contact'}
          </button>
          {showNewContact && (
            <div className="p-3 space-y-2 border-t border-cyan-500/20" onMouseDown={(e) => e.stopPropagation()}>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  placeholder="First name *"
                  className="px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-400/50 text-sm"
                />
                <input
                  type="text"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  placeholder="Last name *"
                  className="px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-400/50 text-sm"
                />
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="Email"
                  className="px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-400/50 text-sm"
                />
                <input
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="Phone"
                  className="px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-400/50 text-sm"
                />
              </div>
              {error && <p className="text-xs text-red-400">{error}</p>}
              <p className="text-xs text-cyan-400/60">Email or phone required.</p>
              <button
                type="button"
                onClick={handleCreateContact}
                disabled={creating}
                className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create & select'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactPicker;
